import config from "environment";
import { getRequest, postRequest, deleteRequest } from "./client";
import {
  AIFormFillResponse,
  AIResponseSave,
  AskContract,
  FeedbackRating,
  RecentAnswer,
  SnippetSimilarity,
  SaveAIResponseContract,
  IndividualType,
  AgentNewThreadMessage,
} from "winslow-core";
import { AskSnippetResponse } from "winslow-core";
import { apiAIAskSnippetPayload } from "./AIService.types";

const { baseApi } = config;

export type AIResponse = {
  answer: string;
  sources: string[];
  linkRecordIds: string[];
};

export async function apiAIAskQuestion(
  payload: AskContract["Body"]
): Promise<AskContract["Reply"]["data"] | null> {
  const fetchURL = `${baseApi}/ai/ask`;
  return await postRequest(fetchURL, payload, "API/apiAIAskQuestion");
}

export async function apiAIAskOpenQuestion(
  workspaceId: string,
  question: string
): Promise<string | null> {
  const fetchURL = `${baseApi}/ai/ask-open-question`;
  return await postRequest(
    fetchURL,
    { workspaceId, question },
    "API/apiAIAskOpenQuestion"
  );
}

export async function apiAIAskReportingQuestion(
  workspaceId: string,
  question: string
): Promise<string | null> {
  const fetchURL = `${baseApi}/ai/ask-reporting-question`;
  return await postRequest(
    fetchURL,
    { workspaceId, question },
    "API/apiAIAskReportingQuestion"
  );
}

export async function apiAIAskUIQuestion(
  workspaceId: string,
  question: string
): Promise<string | null> {
  const fetchURL = `${baseApi}/ai/ask-ui-question`;
  return await postRequest(
    fetchURL,
    { workspaceId, question },
    "API/apiAIAskUIQuestion"
  );
}

export async function apiAIAskAssetQuestion(
  workspaceId: string,
  employeeRecordId: string,
  question: string
): Promise<string | null> {
  const fetchURL = `${baseApi}/ai/ask-asset-question`;
  return await postRequest(
    fetchURL,
    { workspaceId, employeeRecordId, question },
    "API/apiAIAskAssetQuestion"
  );
}

export async function apiAIAskFormFillQuestion(
  workspaceId: string,
  question: string
): Promise<AIFormFillResponse | null> {
  const fetchURL = `${baseApi}/ai/ask-form-fill-question`;
  return await postRequest(
    fetchURL,
    { workspaceId, question },
    "API/apiAIAskFormFillQuestion"
  );
}

export async function apiAIAskSnippet(
  payload: apiAIAskSnippetPayload
): Promise<AskSnippetResponse | null> {
  const fetchURL = `${baseApi}/ai/ask-snippet`;
  console.log("[apiAIAskSnippet] payload: ", payload);
  return await postRequest(fetchURL, payload, "API/apiAIAskSnippet");
}

export async function apiGetSnippets(
  payload: apiAIAskSnippetPayload
): Promise<SnippetSimilarity[] | null> {
  const fetchURL = `${baseApi}/ai/get-snippets`;
  console.log("[apiAIAskSnippet] payload: ", payload);
  return await postRequest(fetchURL, payload, "API/apiAIAskSnippet");
}

export async function apiGenerateSnippet(
  payload: apiAIAskSnippetPayload
): Promise<AskSnippetResponse | null> {
  const fetchURL = `${baseApi}/ai/generate-snippet`;
  console.log("[apiAIAskSnippet] payload: ", payload);
  return await postRequest(fetchURL, payload, "API/apiGenerateSnippet");
}

export async function apiAISaveResponse(
  payload: SaveAIResponseContract["Body"]
): Promise<AIResponseSave | null> {
  const fetchURL = `${baseApi}/ai/save-response`;
  console.log("[AIService] payload: ", payload);
  return await postRequest(fetchURL, payload, "API/apiAISaveResponse");
}

export async function apiAISaveAgentResponse(
  payload: SaveAIResponseContract["Body"]
): Promise<AIResponseSave | null> {
  const fetchURL = `${baseApi}/ai/save-agent-response`;
  console.log("[AIService] payload: ", payload);
  return await postRequest(fetchURL, payload, "API/apiAISaveAgentResponse");
}

// for a user in  workspace
export async function apiAIGetResponseSaves(
  userId: string,
  workspaceId: string
): Promise<AIResponseSave[] | null> {
  const fetchURL = `${baseApi}/ai/saved-responses/${userId}/${workspaceId}`;
  return await getRequest(fetchURL, "API/apiAIGetResponseSaves");
}

export async function apiGetUserRecentAnswers(
  userId: string,
  workspaceId: string
): Promise<RecentAnswer[] | null> {
  const fetchURL = `${baseApi}/ai/recent-answers/${userId}/${workspaceId}`;
  return await getRequest(fetchURL, "API/apiUserRecentAnswersSaves");
}

export async function apiAIGetResponseSavesForUserInCollection(
  userId: string,
  collectionId: string
): Promise<AIResponseSave[] | null> {
  const fetchURL = `${baseApi}/ai/saved-responses-for-user-in-collection/${userId}/${collectionId}`;
  return await getRequest(
    fetchURL,
    "API/apiAIGetResponseSavesForUserInCollection"
  );
}

export async function apiGetWorkspaceAnswers(
  workspaceId: string,
  userId: string
): Promise<RecentAnswer[] | null> {
  const fetchURL = `${baseApi}/workspaces/answers/${workspaceId}/${userId}`;
  return await getRequest(fetchURL, "API/apiGetWorkspaceAnswers");
}

export async function apiAIGetResponseSavesForCollection(
  collectionId: string
): Promise<AIResponseSave[] | null> {
  const fetchURL = `${baseApi}/ai/saved-responses-for-collection/${collectionId}`;
  return await getRequest(fetchURL, "API/apiAIGetResponseSavesForCollection");
}

export async function apiAIGetResponseSavesVisibleToUserInWorkspace(
  userId: string,
  workspaceId: string
): Promise<AIResponseSave[] | null> {
  const fetchURL = `${baseApi}/ai/saved-responses-visible-to-user-in-workspace/${userId}/${workspaceId}`;
  return await getRequest(
    fetchURL,
    "API/apiAIGetResponseSavesVisibleToUserInWorkspace"
  );
}

export async function apiGetRecentAnswersVisibleToUserInWorkspace(
  userId: string,
  workspaceId: string
): Promise<RecentAnswer[] | null> {
  const fetchURL = `${baseApi}/ai/recent-answers-visible-to-user-in-workspace/${userId}/${workspaceId}`;
  return await getRequest(
    fetchURL,
    "API/apiGetRecentAnswersVisibleToUserInWorkspace"
  );
}

export async function aiAIGetResponseSave(
  aiResponseSaveId: string
): Promise<AIResponseSave | null> {
  const fetchURL = `${baseApi}/ai/saved-response/${aiResponseSaveId}`;
  return await getRequest(fetchURL, "API/aiAIGetResponseSave");
}

export async function apiGetConversationHistory(
  userId: string,
  collectionId: string
): Promise<any[] | null> {
  const fetchURL = `${baseApi}/ai/get-conversation-history/${userId}/${collectionId}`;
  return await getRequest(fetchURL, "API/apiGetConversationHistory");
}

export async function apiGetPinnedMessages(
  userId: string
): Promise<any[] | null> {
  const fetchURL = `${baseApi}/ai/get-pinned-messages/${userId}`;
  return await getRequest(fetchURL, "API/apiGetPinnedMessages");
}

export async function apiToggleMessagePin(
  userId: string,
  messageId: number
): Promise<any | null> {
  const fetchURL = `${baseApi}/ai/toggle-message-pin/${userId}/${messageId}`;
  return await postRequest(fetchURL, {}, "API/apiToggleMessagePin");
}

export async function apiGetConversationHistoryPaginated(
  userId: string,
  collectionId: string,
  limit: number = 10,
  offset: number = 0,
  messageId?: number
): Promise<{ 
  messages: any[]; 
  totalCount: number; 
  hasMore: boolean;
  hasMoreUp?: boolean;
  hasMoreDown?: boolean;
} | null> {
  let fetchURL = `${baseApi}/ai/get-conversation-history-paginated/${userId}/${collectionId}?limit=${limit}&offset=${offset}`;
  if (messageId) {
    fetchURL += `&messageId=${messageId}`;
  }
  return await getRequest(fetchURL, "API/apiGetConversationHistoryPaginated");
}

export async function apiGetMessagesAfter(
  userId: string,
  collectionId: string,
  messageId: number,
  limit: number = 10
): Promise<{ messages: any[]; hasMore: boolean } | null> {
  const fetchURL = `${baseApi}/ai/get-messages-after/${userId}/${collectionId}/${messageId}?limit=${limit}`;
  return await getRequest(fetchURL, "API/apiGetMessagesAfter");
}

export async function apiGetMessagesBefore(
  userId: string,
  collectionId: string,
  messageId: number,
  limit: number = 10
): Promise<{ messages: any[]; hasMore: boolean } | null> {
  const fetchURL = `${baseApi}/ai/get-messages-before/${userId}/${collectionId}/${messageId}?limit=${limit}`;
  return await getRequest(fetchURL, "API/apiGetMessagesBefore");
}

export async function apiStartNewThread(
  userId: string,
  collectionId: string,
  personaId: string | null,
  employeeId: string | null
): Promise<AgentNewThreadMessage | null> {
  const fetchURL = `${baseApi}/ai/start-new-thread`;
  return await postRequest(
    fetchURL,
    { userId, collectionId, personaId, employeeId },
    "API/apiStartNewThread"
  );
}

export async function apiTestAction(payload: {
  inputMessage: string;
  userId: string;
  collectionId: string;
  action:
    | "route-intent"
    | "determine-personas"
    | "research-answer"
    | "clarify"
    | "general"
    | "process";
  askAsEmployeeId?: string;
  askAsPersonaId?: string;
}): Promise<AIResponseSave | null> {
  const fetchURL = `${baseApi}/ai/action`;
  return await postRequest(fetchURL, payload, "API/apiTestAction");
}

export async function apiAreMessagesOutOfSync(
  messageId: number,
  sessionId: string
): Promise<boolean | null> {
  const fetchURL = `${baseApi}/ai/are-messages-out-of-sync/${messageId}/${sessionId}`;
  return await getRequest(fetchURL, "API/apiAreMessagesOutOfSync");
}

export async function apiAIUpdateFeedbackRating(payload: {
  messageId: number;
  feedback: string | null;
  rating: number;
  individualId: string;
  individualType: IndividualType;
}): Promise<FeedbackRating | null> {
  const fetchURL = `${baseApi}/ai/update-feedback-rating`;
  return await postRequest(fetchURL, payload, "API/apiAIUpdateFeedbackRating");
}

export async function apiAIDeleteFeedbackRating(
  messageId: number
): Promise<FeedbackRating | null> {
  const fetchURL = `${baseApi}/ai/delete-feedback-rating/${messageId}`;
  return await getRequest(fetchURL, "API/apiAIDeleteFeedbackRating");
}
